import { TranslationKey } from './../../../../../../../types/translation/TranslationKey';
import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { ContainerContext } from '../../../../../../../context/ContainerContext';
import { ContainerTypeContext } from '../../../../../../../context/ContainerTypeContext';
import { CustomFieldContext } from '../../../../../../../context/CustomFieldContext';
import {
  CreateContainerResponse,
  CreateContainerVariables,
  ContainerMutations,
} from '../../../../../../../graphql/container.graphql';
import { SystemBin } from '../../../../../../../types/bin/bin';
import { Container, ContainerInput } from '../../../../../../../types/container';
import { CustomFieldEntityType } from '../../../../../../../types/custom_field/customField';
import { SystemStockLocation } from '../../../../../../../types/stock_location/stockLocation';
import { t } from '../../../../../../../types/translation/Translator';
import Modal from '../../../../../../../VentoryUI/components/common/Modal/Modal';
import { ModalPane } from '../../../../../../../VentoryUI/components/common/Modal/components';
import CustomFieldInput from '../../../../../Common/CustomFieldInput';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../../components/Toast';
import { UserContext } from '../../../../../../../context/UserContext';
import { DropdownSearchSelect } from '../../../../../../components/Dropdown';
import { TextInput } from '../../../../../../components/TextInput';
import { CancelButton, CreateButton } from '../../../../../../components/Button';
import { ContactContext } from '../../../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../../../context/ProductMasterDataContext';

interface CreateOrderContainerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated: (container: Container) => void;
}

export default function CreateOrderCreateContainerModal({ open, setOpen, onCreated }: CreateOrderContainerModalProps) {
  const { showToast } = useToast();
  const { currentCompany } = useContext(CompanyContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { containers, setContainers } = useContext(ContainerContext);
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const containerCustomFields = useMemo(() => {
    return [...customFields.values()].filter(
      cf => cf.entityType === CustomFieldEntityType.container && !cf.hidden && !cf.generated,
    );
  }, [customFields]);

  const [container, setContainer] = useState(
    new ContainerInput({ companyId: currentCompany.id, stockLocationId: SystemStockLocation.id, binId: SystemBin.id }),
  );

  const [create, { loading }] = useMutation<CreateContainerResponse, CreateContainerVariables>(
    ContainerMutations.create,
    {
      onCompleted: data => {
        data.createContainer.forEach(container => containers.set(container.id, new Container(container)));
        if (data.createContainer.length) onCreated(data.createContainer[0]);
        setContainers(new Map(containers));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.containerCreatedSuccessfullyToastTitle).singular.upper,
        });
        handleClose();
      },
    },
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleCreate = async () => {
    await create({
      variables: { containers: [{ ...container, customFields: container.customFieldValues() as any } as any] },
    });
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={t(TranslationKey.createContainer).singular.label}
      width='500px'
    >
      <ModalPane>
        <Grid container gap={2}>
          <Grid item xs={12}>
            <TextInput
              label={t(TranslationKey.identifier).singular.label}
              placeholder={t(TranslationKey.identifier).singular.label}
              value={container.identifier}
              onChange={value => setContainer(container.withIdentifier(value))}
            />
          </Grid>
          <Grid item xs={12}>
            <DropdownSearchSelect
              value={container.containerTypeId}
              onSelect={id => setContainer(container.withContainerTypeId(id || ''))}
              placeholder={t(TranslationKey.selectContainerType).singular.label}
              items={[...containerTypes.values()].map(ct => ct.id)}
              textBuilder={id => containerTypes.get(id)?.name || t(TranslationKey.unknownContainerType).singular.label}
            />
          </Grid>
          {containerCustomFields.length ? (
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {containerCustomFields.map(cf => (
                  <Grid item xs={12}>
                    <CustomFieldInput
                      contacts={contacts}
                      users={companyUsers}
                      item={cf}
                      value={container.customFields.get(cf.id)?.value}
                      onChange={setContainer}
                      entity={container}
                      productMasterData={productMasterData}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ) : null}
          <Grid item xs={12} mt={2}>
            <div className='flex justify-end gap-2'>
              <CancelButton onClick={handleClose} disabled={loading} />
              <CreateButton onClick={handleCreate} loading={loading} disabled={!container.validate(customFields)} />
            </div>
          </Grid>
        </Grid>
      </ModalPane>
    </Modal>
  );
}
